import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Copy, Trash2 } from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import CollapsibleBottomPanel from '~/components/CollapsibleBottomPanel';
import { isApprovalStatusEditable } from '~/domain/dicom/dicomConstants';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import {
    selectedROIState,
    selectionAutoShownState,
} from '~/state/atoms/structureSet/roiAtoms';
import BulkDeletionConfirmationDialog from './BulkDeletionConfirmationDialog';
import CopyStructureSetDialog from './CopyStructureSetDialog/CopyStructureSetDialog';
import { useCopyStructureSetFlow } from './CopyStructureSetDialog/useCopyStructureSetFlow';
import { sortRois } from './roiSort';
import StructureChip from './StructureChip';

type BulkActionsPanelProps = {
    structureSet: StructureSetReadModel;
    selectedRoiNumbers: ReadonlySet<number>;
    hiddenSelectedRoiNumbers: ReadonlySet<number>;
    onClearSelection: () => void;
    onRemoveFromSelection: (roiNumber: number) => void;
};

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
    );
}

type BulkActionButtonProps = {
    icon: ReactNode;
    label: ReactNode;
    color: 'primary' | 'error';
    disabled: boolean;
    onClick: () => void;
    testId: string;
};

function BulkActionButton({
    icon,
    label,
    color,
    disabled,
    onClick,
    testId,
}: BulkActionButtonProps) {
    return (
        <Button
            fullWidth
            variant="outlined"
            color={color}
            size="small"
            disabled={disabled}
            onClick={onClick}
            data-testid={testId}
            sx={{ fontSize: 12, py: 0.5, textTransform: 'none' }}
        >
            <Box
                component="span"
                sx={{ position: 'absolute', left: 8, display: 'inline-flex' }}
            >
                {icon}
            </Box>
            {label}
        </Button>
    );
}

export default function BulkActionsPanel({
    structureSet,
    selectedRoiNumbers,
    hiddenSelectedRoiNumbers,
    onClearSelection,
    onRemoveFromSelection,
}: BulkActionsPanelProps) {
    const intl = useIntl();
    const { removeRois } = useStructureSetCommands();
    const setSelectedROI = useSetRecoilState(selectedROIState);
    const setAutoShown = useSetRecoilState(selectionAutoShownState);
    const selectedROINumber = useRecoilValue(selectedROIState);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);

    const handleCloseCopyDialog = useCallback(() => {
        setCopyOpen(false);
    }, []);

    const onCopySuccess = useCallback(() => {
        setCopyOpen(false);
        onClearSelection();
    }, [onClearSelection]);

    const { patient, sourceImage, handleConfirmCopy } = useCopyStructureSetFlow(
        structureSet,
        onCopySuccess
    );

    const selectedRois = useMemo(
        () =>
            structureSet.roiArray
                .filter((roi) => selectedRoiNumbers.has(roi.roiNumber))
                .sort(sortRois),
        [structureSet.roiArray, selectedRoiNumbers]
    );

    const { deletableRois, nonDeletableRois, deletableRoiNumbers } =
        useMemo(() => {
            const deletable: RoiReadModel[] = [];
            const nonDeletable: RoiReadModel[] = [];
            const deletableNumbers = new Set<number>();
            for (const roi of selectedRois) {
                if (
                    !structureSet.isApproved &&
                    isApprovalStatusEditable(roi.approvalStatus)
                ) {
                    deletable.push(roi);
                    deletableNumbers.add(roi.roiNumber);
                } else {
                    nonDeletable.push(roi);
                }
            }
            return {
                deletableRois: deletable,
                nonDeletableRois: nonDeletable,
                deletableRoiNumbers: deletableNumbers,
            };
        }, [selectedRois, structureSet.isApproved]);

    const handleConfirmDelete = useCallback(() => {
        const deletableNumbers = [...deletableRoiNumbers].sort((a, b) => a - b);
        if (deletableNumbers.includes(selectedROINumber)) {
            setSelectedROI(-1);
            setAutoShown(false);
        }
        setConfirmOpen(false);
        onClearSelection();
        removeRois(structureSet.id, deletableNumbers);
    }, [
        deletableRoiNumbers,
        selectedROINumber,
        setSelectedROI,
        setAutoShown,
        onClearSelection,
        removeRois,
        structureSet.id,
    ]);

    const deleteDisabled =
        deletableRois.length === 0 || structureSet.isApproved;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (confirmOpen || copyOpen || isEditableTarget(event.target)) {
                return;
            }
            if (event.key === 'Escape') {
                onClearSelection();
            } else if (event.key === 'Delete' && !deleteDisabled) {
                setConfirmOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [confirmOpen, copyOpen, onClearSelection, deleteDisabled]);
    const copyDisabled = !patient || !sourceImage;

    const deleteButton = (
        <BulkActionButton
            icon={<Trash2 size={14} />}
            label={<FormattedMessage id="structure.bulk.deleteButton" />}
            color="error"
            disabled={deleteDisabled}
            onClick={() => setConfirmOpen(true)}
            testId="bulk-delete-button"
        />
    );

    return (
        <>
            <CollapsibleBottomPanel
                defaultExpanded
                onClose={onClearSelection}
                closeLabel={intl.formatMessage({
                    id: 'structure.bulk.clearSelection',
                })}
                header={
                    <Typography
                        aria-live="polite"
                        sx={{ fontSize: 13, fontWeight: 500 }}
                    >
                        <FormattedMessage
                            id="structure.bulk.selectedCount"
                            values={{
                                count: selectedRois.length,
                                total: structureSet.roiArray.length,
                            }}
                        />
                    </Typography>
                }
            >
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={0.5}
                    mb={0.5}
                    sx={{ maxHeight: 88, overflowY: 'auto' }}
                    data-testid="bulk-chip-list"
                >
                    {selectedRois.map((roi) => (
                        <StructureChip
                            key={roi.roiNumber}
                            roi={roi}
                            testId={`bulk-chip-${roi.roiNumber}`}
                            isHidden={hiddenSelectedRoiNumbers.has(
                                roi.roiNumber
                            )}
                            isLocked={!deletableRoiNumbers.has(roi.roiNumber)}
                            onRemove={onRemoveFromSelection}
                        />
                    ))}
                </Box>
                {hiddenSelectedRoiNumbers.size > 0 && (
                    <Typography
                        variant="caption"
                        color="warning.main"
                        data-testid="bulk-hidden-caption"
                    >
                        <FormattedMessage
                            id="structure.bulk.hiddenByFilters"
                            values={{ count: hiddenSelectedRoiNumbers.size }}
                        />
                    </Typography>
                )}
                {nonDeletableRois.length > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="bulk-approved-caption"
                    >
                        <FormattedMessage
                            id="structure.bulk.approvedCount"
                            values={{ count: nonDeletableRois.length }}
                        />
                    </Typography>
                )}
                <Box display="flex" flexDirection="column" gap={0.75} mt={0.75}>
                    <BulkActionButton
                        icon={<Copy size={14} />}
                        label={
                            <FormattedMessage id="structure.bulk.copyButton" />
                        }
                        color="primary"
                        disabled={copyDisabled}
                        onClick={() => setCopyOpen(true)}
                        testId="bulk-copy-button"
                    />
                    {deleteDisabled ? (
                        <Tooltip
                            title={intl.formatMessage({
                                id: structureSet.isApproved
                                    ? 'structure.bulk.deleteDisabled.structureSetApproved'
                                    : 'structure.bulk.deleteDisabled.approved',
                            })}
                        >
                            <span style={{ display: 'flex', width: '100%' }}>
                                {deleteButton}
                            </span>
                        </Tooltip>
                    ) : (
                        deleteButton
                    )}
                </Box>
            </CollapsibleBottomPanel>
            <BulkDeletionConfirmationDialog
                open={confirmOpen}
                deletableRois={deletableRois}
                nonDeletableRois={nonDeletableRois}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmOpen(false)}
            />
            {copyOpen && patient && sourceImage && (
                <CopyStructureSetDialog
                    open={copyOpen}
                    onClose={handleCloseCopyDialog}
                    onConfirm={handleConfirmCopy}
                    sourceStructureSet={structureSet}
                    patient={patient}
                    sourceImage={sourceImage}
                    initialSelectedRoiNumbers={selectedRoiNumbers}
                />
            )}
        </>
    );
}
